// Origin resolvers for the Roadmapper SPA. Identity (app_id / app name) is NOT
// resolved here — the shared @topolo-io/worker-runtime injects
// window.__TOPOLO_APP_ID__ from the Developers Catalog and the browser reads it
// via getAppId()/getAppName() from @topolo-io/app-shell. This module only maps
// sibling-service origins for same-environment fetches.

function getEnvValue(...keys: string[]): string | undefined {
  const env = (import.meta as any).env || {};
  for (const key of keys) {
    const value = env[key];
    if (typeof value === 'string' && value.trim() && !value.includes('%')) {
      return value.trim().replace(/\/+$/, '');
    }
  }
  return undefined;
}

function isStagingRuntime(): boolean {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    return host.endsWith('.stg.topolo.us') || host.includes('topolo-staging.workers.dev');
  }
  const mode = getEnvValue('MODE', 'VITE_ENVIRONMENT', 'ENVIRONMENT');
  return mode === 'staging';
}

function topoloOrigin(service: string): string {
  return isStagingRuntime()
    ? `https://${service}.stg.topolo.us`
    : `https://${service}.topolo.app`;
}

function isProductionTopoloOrigin(value: string): boolean {
  try {
    return new URL(value).hostname.endsWith('.topolo.app');
  } catch {
    return false;
  }
}

function isStagingTopoloOrigin(value: string): boolean {
  try {
    const hostname = new URL(value).hostname;
    return hostname.endsWith('.stg.topolo.us') || hostname.includes('topolo-staging.workers.dev');
  } catch {
    return false;
  }
}

function resolveServiceOrigin(service: string, ...envKeys: string[]): string {
  const fallback = topoloOrigin(service);
  const envValue = getEnvValue(...envKeys);
  if (isStagingRuntime() && envValue && isProductionTopoloOrigin(envValue)) {
    return fallback;
  }
  if (!isStagingRuntime() && envValue && isStagingTopoloOrigin(envValue)) {
    return fallback;
  }
  return envValue || fallback;
}

export function getRoadmapperApiBaseUrl(): string {
  // Canonical front-door: the SPA calls its own origin under /api (the web
  // worker proxies /api to the Roadmapper API worker). No dedicated
  // roadmapper-api host. Local dev hits the api worker directly; SSR falls
  // back to the app origin.
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8787/api';
    }
    return `${origin}/api`;
  }
  return `${resolveServiceOrigin('roadmapper', 'VITE_APP_ORIGIN', 'VITE_FRONTEND_URL')}/api`;
}

export function getRoadmapperAppUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return resolveServiceOrigin('roadmapper', 'VITE_FRONTEND_URL', 'VITE_APP_ORIGIN');
}

export function getBugfixApiUrl(): string {
  return resolveServiceOrigin('bugfix', 'VITE_BUGFIX_API_URL', 'VITE_TOPOLO_BUG_REPORTER_URL');
}
